import React from "react";
import BusinessCaseBuilder from "./landing/business-case/BusinessCaseBuilder";
import {
  CalculationExplainer,
  UsingYourCase,
  WorkedExampleSection,
  type WorkedExample,
} from "./landing/business-case/BusinessCaseGuide";
import type { Evidence, Pricing } from "./landing/business-case/calculator";
import { LeadFormProvider } from "./landing/lead-form/LeadFormProvider";
import { Breadcrumb, CONTAINER, SectionLabel } from "./landing/ui";

type Props = {
  pricing: Pricing;
  evidence: Evidence;
  workedExample: WorkedExample;
};

export default function BusinessCasePage({ pricing, evidence, workedExample }: Props) {
  return (
    <LeadFormProvider>
      <section className="tw-bg-[#10233f] tw-pb-16 tw-pt-24 tw-text-white sm:tw-pb-12 sm:tw-pt-12">
        <div className={CONTAINER}>
          <Breadcrumb current="Business case builder" tone="dark" />
          {/* The approved H1, mirrored in static_pages/business_case.html.erb; keep both in step. */}
          <h1 className="tw-mt-6 tw-mb-0 tw-max-w-5xl tw-text-[clamp(2.75rem,5.1vw,4.5rem)] sm:tw-text-[clamp(2.25rem,8vw,2.75rem)] tw-font-black tw-leading-[1.02] tw-tracking-[-.05em]">
            Mentoring ROI calculator: build a credible business case for your programme
          </h1>
          <p className="tw-mt-6 tw-mb-0 tw-max-w-3xl tw-text-lg tw-leading-8 tw-text-[#d9e7f1]">
            Estimate the retention value, productivity gains, leadership or onboarding value and admin
            time a mentoring programme could bring, using your own figures. You get a planning case you
            can take to leadership and an indicative annual quote.
          </p>
        </div>
      </section>

      <section id="roi-calculator" className="tw-scroll-mt-24 tw-bg-[#eef7fd] tw-py-28 sm:tw-py-16">
        <div className={CONTAINER}>
          <div className="tw-mx-auto tw-max-w-4xl tw-text-center">
            <SectionLabel>Mentoring business case builder</SectionLabel>
            <p className="tw-mt-4 tw-mb-0 tw-text-lg tw-leading-8 tw-text-[#60768c] xs:tw-text-base xs:tw-leading-7">
              Choose your goal and see the potential value. You&rsquo;ll get a business case and an
              indicative annual quote to take to your team.
            </p>
          </div>

          <div className="tw-mt-12 sm:tw-mt-9">
            <BusinessCaseBuilder pricing={pricing} evidence={evidence} />
          </div>
        </div>
      </section>

      <CalculationExplainer />
      <WorkedExampleSection example={workedExample} />
      <UsingYourCase />
    </LeadFormProvider>
  );
}
